import { useGetOrders } from '@vindr/services/actions/order.action';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { MATCH_STATUS } from '@vindr/services/constants/study.constant';
import { PaginationProps } from '@vindr/services/interfaces';
import { ParamsGetOrders, ResponseGetOrders } from '@vindr/services/src/apis/order.api';
import { Table } from '@vindr/ui';
import { addToArray } from '@vindr/utils/helpers/common.helper';
import onClickRow, { onDoubleClickRow } from '@vindr/utils/state/onClickRow';
import onPaginationChange from '@vindr/utils/state/onPaginationChange';
import { Button } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import Filter from './Filter';
import useOrderSearchColumns from './useOrderSearchColumns';

const OrderSearch = ({
  selectedOrders,
  onSave,
  onCancel,
  initFilters,
  message,
}: {
  selectedOrders: ResponseGetOrders['data'];
  onSave: (value: ResponseGetOrders['data']) => void;
  onCancel: Parameters<typeof Button>['0']['onClick'];
  initFilters: ParamsGetOrders;
  message: MessageInstance;
}) => {
  const columns = useOrderSearchColumns();
  const [params, setParams] = useState(initFilters);
  const { t } = useTranslation('Vindoc');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useImmer<PaginationProps>({
    page: 1,
    size: 100,
  });
  const { data, isFetching, isLoading, refetch } = useGetOrders({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    ...params,
    match_status: MATCH_STATUS.Unmatched, //? With no study_uid
    status: [
      ORDER_STATUS.APPROVED,
      ORDER_STATUS.ARRIVED,
      ORDER_STATUS.NEW,
      ORDER_STATUS.READING,
      ORDER_STATUS.SUBMITTED,
    ],
  });
  const count = data?.count;
  const orderList = data?.data;

  const [isRefetch, setIsRefetch] = useState(false);
  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const forceSearch = (params) => {
    setParams(params);
    // Set refetch to true only when force search
    setIsRefetch(true);
    refetch?.();
  };

  useEffect(() => {
    setSelectedRowKeys(selectedOrders);
  }, [selectedOrders]);

  const handleSave = useCallback(
    (record?: any) => {
      const selected = addToArray(record || selectedRowKeys, []);

      onSave(selected);
    },
    [onSave, selectedRowKeys]
  );

  return (
    <div className="flex flex-col">
      <Filter
        forceSearch={forceSearch}
        initFilters={initFilters}
        onChange={(p) => {
          setParams(p);
        }}
      />
      <Table
        columns={columns}
        count={count}
        dataSource={(orderList as any) || []}
        loading={isLoading || isRefetch}
        pagination={count < pagination.size ? null : pagination}
        rowKey="id"
        scroll={{ y: 500 }}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        wrapperClassName="h-auto"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: addToArray(selectedRowKeys, []).map((x) => x.id),
          onChange: (keys, records) => {
            if (
              records &&
              ((selectedOrders?.length &&
                !records.every(
                  (it) =>
                    it?.visited_patient?.visit_id === selectedOrders[0]?.visited_patient.visit_id
                )) ||
                !records.every(
                  (it) => it?.visited_patient?.visit_id === records[0]?.visited_patient.visit_id
                ))
            ) {
              message.error(t('Visit ID mismatch'));

              return;
            }
            setSelectedRowKeys(records);
          },
        }}
        onPaginationChange={(p) => {
          setPagination((draft) => onPaginationChange(draft, p));
        }}
        onRow={(record) => {
          return {
            onClick: (event) => onClickRow({ event, setSelectedRowKeys, record }),
            onDoubleClick: (event) =>
              onDoubleClickRow({ event, callback: () => handleSave(record) }),
          };
        }}
      />
      <div className="footer mt-3 flex justify-end">
        <Button onClick={onCancel}>{t('Cancel')}</Button>
        <Button className="ml-2" type="primary" onClick={() => handleSave()}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default OrderSearch;
